import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { z } from "zod"
import { API_BASE_URL } from "@/lib/api"

// Zod schema for login validation
const loginSchema = z.object({
  userId: z.string().min(1, "User ID is required"),
  pin: z.string().min(4, "PIN must be at least 4 digits").regex(/^\d+$/, "PIN must contain only numbers"),
})

// API base URL
// const API_BASE_URL = "https://api-dear-lottery.threecircle5.com/api"
// Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Types for API response
interface LoginUser {
  id: string
  userId: string
  role: string
  balance: number
}

interface LoginResponse {
  token: string
  user: LoginUser
}

// Secure storage utilities
const storage = {
  setItem: (key: string, value: string) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(key, value)
      }
    } catch (error) {
      console.error('Error storing data:', error)
    }
  },
  
  getItem: (key: string): string | null => {
    try {
      if (typeof window !== 'undefined') {
        return localStorage.getItem(key)
      }
      return null
    } catch (error) {
      console.error('Error retrieving data:', error)
      return null
    }
  },
  
  removeItem: (key: string) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(key)
      }
    } catch (error) {
      console.error('Error removing data:', error)
    }
  }
}

// Auth utilities
export const auth = {
  // Store authentication data
  login: (token: string, user: LoginUser) => {
    storage.setItem('auth_token', token)
    storage.setItem('user_data', JSON.stringify(user))
    
    // Also set default authorization header for future requests
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`
  },
  
  // Get authentication data
  getToken: (): string | null => {
    return storage.getItem('auth_token')
  },
  
  getUser: (): LoginUser | null => {
    const userData = storage.getItem('user_data')
    if (userData) {
      try {
        return JSON.parse(userData)
      } catch (error) {
        console.error('Error parsing user data:', error)
        return null
      }
    }
    return null
  },
  
  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!auth.getToken()
  },
  
  // Check if user is admin
  isAdmin: (): boolean => {
    const user = auth.getUser()
    return user?.role === 'ADMIN'
  },
  
  // Logout
  logout: () => {
    storage.removeItem('auth_token')
    storage.removeItem('user_data')
    delete api.defaults.headers.common['Authorization']
  },
  
  // Initialize auth from storage (call this on app start)
  initialize: () => {
    const token = auth.getToken()
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    }
  }
}

export default function LoginPage() {
  const [form, setForm] = useState({
    userId: "",
    pin: "",
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isPending, setIsPending] = useState(false)
  const [submitError, setSubmitError] = useState("")

  const navigate = useNavigate()

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }))
    }
    if (submitError) {
      setSubmitError("")
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError("")
    setErrors({})
    setIsPending(true)

    // Validate with Zod
    const result = loginSchema.safeParse(form)
    if (!result.success) {
      const fieldErrors: { [key: string]: string } = {}
      result.error.errors.forEach((err) => {
        if (err.path.length > 0) {
          fieldErrors[err.path[0]] = err.message
        }
      })
      setErrors(fieldErrors)
      setIsPending(false)
      return
    }

    try {
      // Make API call to login
      const response = await api.post<LoginResponse>("/auth/login", result.data)
      
      if (response.data.token && response.data.user) {
        // Store token and user data securely
        auth.login(response.data.token, response.data.user)
        
        // Redirect based on user role
        if (response.data.user.role === 'ADMIN') {
          navigate("/DearLottery/admin")
        } else if (response.data.user.role === 'DISTRIBUTOR') {
          navigate("/DearLottery/admin")
        } else if (response.data.user.role === 'AGENT') {
          navigate("/DearLottery/admin")
        } else {
          navigate("/DearLottery/single/add-single")
        }
      } else {
        setSubmitError("Login failed. Invalid response from server.")
      }
    } catch (error: any) {
      console.error("Login error:", error)
      
      // Handle different types of errors
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Server responded with error status
          const serverError = error.response.data
          setSubmitError(serverError.error || "Invalid credentials. Please try again.")
        } else if (error.request) {
          // Request was made but no response received
          setSubmitError("Network error. Please check your connection and try again.")
        } else {
          // Something else happened
          setSubmitError("An unexpected error occurred. Please try again.")
        }
      } else {
        // Non-Axios error
        setSubmitError("An unexpected error occurred. Please try again.")
      }
    } finally {
      setIsPending(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#2a003a] to-[#1a002a] px-4 py-8 relative overflow-hidden">
      {/* Background glow effects */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-600 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-pink-600 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>

      <Card className="w-full max-w-md bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl shadow-2xl text-white relative z-10">
        <CardHeader className="text-center pt-8 pb-6">
          <img
            src="../assets/logo.jpg"
            alt="Three Circle 7 Logo"
            className="mx-auto h-16 w-16 rounded-full mb-4 shadow-lg border border-white/20"
          />
          <CardTitle className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-green-400">
            Welcome to Three Circle 7
          </CardTitle>
          <CardDescription className="text-white/80 mt-2">
            Enter your credentials to continue.
          </CardDescription>
        </CardHeader>

        <CardContent className="pb-8">
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div className="grid gap-2">
              <Label htmlFor="userId" className="text-white/90">
                User ID
              </Label>
              <Input
                id="userId"
                name="userId"
                type="text"
                placeholder="Enter your ID"
                required
                value={form.userId}
                onChange={handleChange}
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/60 focus:ring-purple-500 focus:border-purple-500"
              />
              {errors.userId && (
                <p className="text-red-400 text-sm mt-1">{errors.userId}</p>
              )}
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="pin" className="text-white/90">
                PIN
              </Label>
              <Input
                id="pin"
                name="pin"
                type="password"
                placeholder="Enter PIN"
                required
                maxLength={4}
                value={form.pin}
                onChange={handleChange}
                className="bg-white/10 border border-white/30 text-white placeholder:text-white/60 focus:ring-purple-500 focus:border-purple-500"
              />
              {errors.pin && (
                <p className="text-red-400 text-sm mt-1">{errors.pin}</p>
              )}
            </div>
            
            {submitError && (
              <p className="text-red-400 text-sm text-center">{submitError}</p>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-red-500 to-green-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold py-3 rounded-lg shadow-lg transition-all duration-300"
              disabled={isPending}
            >
              {isPending ? "Logging In..." : "Login"}
            </Button>

            {/* Register link below */}
            <p className="text-center text-white/70 text-sm mt-4">
              No account?{" "}
              <button
                type="button"
                onClick={() => navigate("/DearLottery/register")}
                className="text-green-400 hover:text-green-600 font-semibold underline underline-offset-2 cursor-pointer"
              >
                Register here.
              </button>
            </p>
          </form>
          
          <p className="text-center text-white/60 text-sm mt-8">
            {"© 2025 Three Circle 7. All rights reserved."}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}